import React, { useEffect, useMemo, useState } from 'react';
import { cssMap } from '@compiled/react';
import Select, { components, type CSSObjectWithLabel, type OptionProps } from '@atlaskit/select';
import { Field, useFormState } from '@atlaskit/form';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { defineMessages, useIntl } from '@atlassian/jira-intl';
import type { ShareDialogFormData } from '../types.tsx';
import type { Props, UserRoleSelect } from './types.tsx';

const styles = cssMap({
	userRoleSelect: {
		flexGrow: 1,
		flexBasis: '0',
		minWidth: '112px',
		paddingTop: token('space.250'),
		marginTop: token('space.025'),
	},
});

const messages = defineMessages({
	userRoleLabel: {
		id: 'share-button.user-role.user-role-label.non-final',
		defaultMessage: 'User role',
		description: 'Label for the user role select field',
	},
});

const SelectOption = ({ children, ...props }: OptionProps<UserRoleSelect>) => {
	const { data } = props;

	if (!data) return null;

	return (
		<components.Option {...props}>
			<Stack space="space.050">
				<Text size="medium">{data.label}</Text>
				<Text size="small" color="color.text.subtle">
					{data.description}
				</Text>
			</Stack>
		</components.Option>
	);
};

const getDefaultUserRole = (userRoleOptions: UserRoleSelect[]) =>
	userRoleOptions.find((role) => role.default) || userRoleOptions[0];

export const UserRole = ({ accessLevel, roles, isLoading }: Props) => {
	const { formatMessage } = useIntl();

	const formData = useFormState<ShareDialogFormData>({
		values: true,
	});
	const shouldShowUserRole = useMemo(() => {
		const projectAccessLevel = formData?.values.projectAccessLevel?.value || accessLevel;
		return roles.length > 0 && projectAccessLevel && projectAccessLevel !== 'OPEN';
	}, [accessLevel, formData?.values.projectAccessLevel?.value, roles.length]);

	const userRoleOptions = useMemo<UserRoleSelect[]>(
		() =>
			roles.map((role) => ({
				label: role.name,
				value: role.id,
				description: role.description,
				default: role.default || false,
			})),
		[roles],
	);

	const [currentUserRole, setCurrentUserRole] = useState<UserRoleSelect>(
		getDefaultUserRole(userRoleOptions),
	);

	useEffect(() => {
		if (!currentUserRole && userRoleOptions.length > 0) {
			// If currentUserRole is not set, we set it to the default role from options.
			// This happens when the component is rendered before userRoleOptions are fetched.
			setCurrentUserRole(getDefaultUserRole(userRoleOptions));
		}
	}, [currentUserRole, userRoleOptions]);

	if (!shouldShowUserRole) {
		return null;
	}

	return (
		<Field<UserRoleSelect> name="userRole" defaultValue={currentUserRole}>
			{({ fieldProps: { onChange, value, ...fieldProps } }) => (
				<Box xcss={styles.userRoleSelect}>
					<Select<UserRoleSelect>
						{...fieldProps}
						isLoading={isLoading}
						options={userRoleOptions}
						value={currentUserRole}
						onChange={(userRoleOption: UserRoleSelect | null) => {
							if (!userRoleOption) return;

							onChange(userRoleOption);
							setCurrentUserRole(userRoleOption);
						}}
						label={formatMessage(messages.userRoleLabel)}
						components={{
							Option: SelectOption,
						}}
						// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
						styles={{
							menuPortal: (base: CSSObjectWithLabel) => ({
								...base,
								zIndex: 9999,
							}),
							control: (base: CSSObjectWithLabel) => ({
								...base,
								minHeight: '44px',
								height: '100%',
								width: '100%',
							}),
							menu: (base: CSSObjectWithLabel) => ({
								...base,
								width: 'fit-content',
								position: 'absolute',
								right: 0,
							}),
							option: (base: CSSObjectWithLabel) => ({
								...base,
								width: '320px',
							}),
						}}
						menuPortalTarget={globalThis.document.body}
					/>
				</Box>
			)}
		</Field>
	);
};
